import type { ButtonHTMLAttributes, ReactNode } from "react";
import { AlertCircle, CheckCircle2, LoaderCircle, X } from "lucide-react";

export function Button({ variant = "primary", className = "", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "danger" | "ghost" }) {
  return <button className={`button button--${variant} ${className}`} {...props} />;
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow: string; title: string; description?: string; actions?: ReactNode }) {
  return <header className="page-header">
    <div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1>{description && <p className="page-description">{description}</p>}</div>
    {actions && <div className="page-actions">{actions}</div>}
  </header>;
}

export function EmptyState({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return <div className="empty"><div className="empty-mark">UBP</div><h3>{title}</h3><p>{children}</p>{action}</div>;
}

export function Loading() {
  return <div className="loading"><LoaderCircle aria-hidden className="spin" /> Cargando información…</div>;
}

export function ErrorNotice({ message }: { message: string }) {
  return <div className="notice notice--error"><AlertCircle aria-hidden /><span>{message}</span></div>;
}

export function SuccessNotice({ message }: { message: string }) {
  return <div className="notice notice--success"><CheckCircle2 aria-hidden /><span>{message}</span></div>;
}

export function Modal({ title, description, children, onClose, width = "normal" }: { title: string; description?: string; children: ReactNode; onClose: () => void; width?: "normal" | "wide" }) {
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className={`modal modal--${width}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <header className="modal-header"><div><h2 id="modal-title">{title}</h2>{description && <p>{description}</p>}</div><button className="icon-button" onClick={onClose} aria-label="Cerrar"><X /></button></header>
      {children}
    </section>
  </div>;
}

export function ConfirmDialog({ title, children, confirmLabel, destructive = false, onConfirm, onClose }: { title: string; children: ReactNode; confirmLabel: string; destructive?: boolean; onConfirm: () => void | Promise<void>; onClose: () => void }) {
  return <Modal title={title} onClose={onClose}>
    <div className="modal-body"><p>{children}</p></div>
    <footer className="modal-footer"><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button variant={destructive ? "danger" : "primary"} onClick={onConfirm}>{confirmLabel}</Button></footer>
  </Modal>;
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return <label className="field"><span className="field-label">{label}</span>{children}{hint && <span className="field-hint">{hint}</span>}{error && <span className="field-error">{error}</span>}</label>;
}

export function StatusBadge({ status }: { status: string | null }) {
  const labels: Record<string, string> = { completed: "Realizada", reviewed: "Revisada", pending: "Pendiente", incident: "Incidencia" };
  return <span className={`status status--${status ?? "unmarked"}`}>{status ? labels[status] : "Sin estado"}</span>;
}

export function Segmented({ value, onChange, options }: { value: string; onChange: (value: string) => void; options: { value: string; label: string }[] }) {
  return <div className="segmented">{options.map((option) => <button type="button" key={option.value} className={value === option.value ? "active" : ""} onClick={() => onChange(option.value)}>{option.label}</button>)}</div>;
}
